import { useStore } from '@/containers'
import { IconClose, SearchIcon } from '@/styles/Icon'
import type { Province } from '@/types'
import { nonAccentVietnamese } from '@/utils/converter'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { setCookie } from 'cookies-next'
import { Fragment, useEffect, useState, type ChangeEvent } from 'react'

export interface FormAddressProps {
  value: string
  name: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  className?: string
  type?: string
  classSelectButtonName?: string
  placeholder: string
  placeholderSearch: string
  isError?: boolean | string
  isICon?: boolean
  zIndex?: string
  defaultTextColor?: string
  onDropdownOpen?: () => void
  title: string
  data: Province[]
}

export function FormAddress({
  placeholder,
  title,
  className,
  type,
  name,

  onChange,
  defaultTextColor = '',
  value,
  placeholderSearch = '',
  isError = false,
  classSelectButtonName = '',
  isICon = true,
  data,
}: FormAddressProps) {
  const [selected, setSelected] = useState<Province | undefined>()
  const {
    setRegionChange,
    setIsChangeRegionModal,
    currentRegion,
    getDistricts,
  } = useStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const filteredOptions = searchTerm
    ? data.filter(item =>
        nonAccentVietnamese(item.name).includes(nonAccentVietnamese(searchTerm))
      )
    : data
  const onListBoxChange = async (listBoxValue: Province | undefined) => {
    onChange({
      target: { value: listBoxValue?.code, name },
    } as ChangeEvent<HTMLInputElement>)
    setSelected(listBoxValue)

    if (
      name == 'provinceCode' &&
      listBoxValue?.saleRegionCode &&
      listBoxValue?.saleRegionCode != currentRegion &&
      type == 'cart'
    ) {
      getDistricts(listBoxValue?.code)
      setIsChangeRegionModal(true)
      localStorage.setItem('province', listBoxValue?.code)
      setRegionChange(listBoxValue?.saleRegionCode)
      setCookie('province', listBoxValue?.code)
      await setCookie('sale-region', listBoxValue?.saleRegionCode)
    }
    setSearchTerm('')
    setIsOpen(false)
  }
  useEffect(() => {
    if (value && data) {
      const valueDefault = data?.find(item => item.code === value)
      if (valueDefault) {
        setSelected(valueDefault)
      }
    }
    if (value == '') {
      setSelected(undefined)
    }
  }, [value, data])

  return (
    <div className={`relative ${className}`}>
      <button
        type='button'
        onClick={() => setIsOpen(true)}
        className={`flex w-full items-center justify-start text-sm border ${
          isError ? 'border-states-error focus:ring-gray-light' : ''
        } focus:ring-gray-medium rounded min-h-12 py-2 pl-3 ${classSelectButtonName} ${
          !isICon ? 'pr-3' : ''
        }`}
      >
        <div className={`truncate ${selected ? defaultTextColor : ''}`}>
          {selected ? (
            selected?.name
          ) : (
            <span className='text-gray-900'>{placeholder}</span>
          )}
        </div>
        {isICon && (
          <div className={`ml-auto px-3`}>
            {isOpen ? (
              <IconDown className='rotate-180' />
            ) : (
              <IconUp className='' />
            )}
          </div>
        )}
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as='div'
          className={`relative z-[9999]`}
          onClose={() => setIsOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter='transition-opacity ease-linear duration-400'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity ease-linear duration-400'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 z-[999] left-0 bottom-0 bg-black-150 transition-opacity' />
          </Transition.Child>
          <div className='pointer-events-none z-[999] w-full overflow-hidden fixed left-[50%] translate-x-[-50%] rounded-tr-sm rounded-tl-sm bottom-0 flex max-h-[55vh] h-[55vh] max-w-[440px] '>
            <Transition.Child
              as={Fragment}
              enter='transition ease-in-out duration-700 transform'
              enterFrom='translate-y-full'
              enterTo='translate-y-0'
              leave='transition ease-in-out duration-700 transform'
              leaveFrom='translate-y-0'
              leaveTo='translate-y-full'
            >
              <Dialog.Panel className='pointer-events-auto w-full h-[55vh] max-w-[440px]'>
                <div className={`bg-white block overflow-hidden h-full w-full`}>
                  <div className='headerModal w-full h-[60px] px-3 pt-4 pb-3 relative'>
                    <Dialog.Title as='h2' className={`text-titleSection`}>
                      {title}
                    </Dialog.Title>
                    <button
                      onClick={() => setIsOpen(false)}
                      type='button'
                      className='absolute right-3 top-4 cursor-pointer z-10'
                    >
                      <IconClose />
                    </button>
                  </div>
                  <div className={`w-full px-1`}>
                    <div className='overflow-auto bg-white text-base focus:outline-none transition'>
                      <div className='relative bg-white-400'>
                        <div className='relative z-20 w-full bg-white-400 px-2'>
                          <div className='absolute left-4 top-[50%] flex translate-y-[-50%]'>
                            <SearchIcon />
                          </div>
                          <div className='block items-center'>
                            <input
                              type='search'
                              name='searchAddress'
                              autoFocus={true}
                              id='searchAddress'
                              value={searchTerm}
                              placeholder={placeholderSearch}
                              onKeyDown={e => {
                                if (e.keyCode === 32) {
                                  e.stopPropagation()
                                }
                              }}
                              onChange={e => setSearchTerm(e.target.value)}
                              className='text-sm block w-full rounded-full border border-white-500 bg-white-500 pl-12 h-10 pr-6 placeholder:text-sm outline-none focus:border-orange transition focus:outline-none'
                            />
                            <div className='absolute right-3 top-[50%] flex translate-y-[-50%] py-1.5 pr-1.5 '>
                              {searchTerm && (
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  className='mr-1 h-4 w-4 font-bold text-orange'
                                  viewBox='0 0 20 20'
                                  fill='currentColor'
                                  onClick={() => {
                                    setSearchTerm('')
                                  }}
                                >
                                  <path
                                    fillRule='evenodd'
                                    d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                    clipRule='evenodd'
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                        <ul className='resultList content-start mt-3 overflow-y-auto max-h-[calc(55vh-112px)] min-h-[calc(55vh-112px)] text-sm pb-6 scrollBarCS h-full transition mr-1 pl-2 pr-1'>
                          {filteredOptions && filteredOptions.length == 0 && (
                            <p className='mt-2 text-center'>Không có dữ liệu</p>
                          )}
                          {filteredOptions &&
                            filteredOptions.length > 0 &&
                            filteredOptions.map(addItem => (
                              <div
                                key={addItem._id}
                                className={clsx(
                                  'relative cursor-pointer text-center select-none py-2 pl-3 pr-9 border-b-[1px] border-white-500'
                                )}
                                onClick={() => onListBoxChange(addItem)}
                              >
                                <span
                                  className={clsx(
                                    !isOpen ? 'font-semibold' : 'font-normal',
                                    'block truncate'
                                  )}
                                >
                                  {addItem.name}
                                </span>
                              </div>
                            ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default FormAddress

interface IconUp {
  className?: string
}

function IconUp({ className = '' }: IconUp) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
interface IconDown {
  className?: string
  width?: number
  height?: number
}

function IconDown({ className = 'fill-black' }: IconDown) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
